import React, { useState, useEffect } from 'react';
import { FaBell, FaTimes, FaCheck, FaCalendarAlt, FaUser, FaPhone, FaEnvelope } from 'react-icons/fa';
import { toast } from 'react-toastify';

const NotificationCenter = ({ isAuthenticated, user }) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Función para obtener notificaciones
  const fetchNotificaciones = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notificaciones`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotificaciones(data);
        
        // Contar no leídas
        const noLeidasCount = data.filter(notif => 
          !notif.leida.some(leida => leida.usuario === user?.id)
        ).length;
        setNoLeidas(noLeidasCount);
      }
    } catch (error) {
      if (error?.response?.status === 401 || error?.message?.includes('401')) {
        toast.error('Tu sesión ha expirado, por favor inicia sesión de nuevo.');
        return;
      }
      console.error('Error al obtener notificaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para marcar como leída
  const marcarComoLeida = async (notificacionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notificaciones/${notificacionId}/leer`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Actualizar estado local
        setNotificaciones(prev => 
          prev.map(notif => 
            notif._id === notificacionId 
              ? { ...notif, leida: [...notif.leida, { usuario: user?.id || user?._id, fecha: new Date() }] }
              : notif
          )
        );
        setNoLeidas(prev => Math.max(0, prev - 1));
        // Refrescar notificaciones desde el backend
        fetchNotificaciones();
      }
    } catch (error) {
      console.error('Error al marcar como leída:', error);
    }
  };

  // Función para marcar todas como leídas
  const marcarTodasComoLeidas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notificaciones/leer-todas`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setNotificaciones(prev => 
          prev.map(notif => ({
            ...notif,
            leida: [...notif.leida, { usuario: user?.id, fecha: new Date() }]
          }))
        );
        setNoLeidas(0);
      }
    } catch (error) {
      console.error('Error al marcar todas como leídas:', error);
    }
  };

  // Verificar si una notificación está leída
  const isLeida = (notificacion) => {
    return notificacion.leida.some(leida => String(leida.usuario) === String(user?.id || user?._id));
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchNotificaciones();
    }
  }, [isAuthenticated, user]);

  // Polling para actualizar notificaciones cada 5 segundos
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(() => {
      fetchNotificaciones();
    }, 5000); // cada 5 segundos

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  return (
    <div className="relative">
      {/* Botón de notificaciones */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-[#1B65A6] transition-colors"
        aria-label="Notificaciones"
      >
        <FaBell className="w-6 h-6" />
        {noLeidas > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        )}
      </button>

      {/* Panel de notificaciones */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Notificaciones</h3>
            <div className="flex items-center gap-2">
              {noLeidas > 0 && (
                <button
                  onClick={marcarTodasComoLeidas}
                  className="text-sm text-[#1B65A6] hover:underline"
                >
                  Marcar todas como leídas
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Lista de notificaciones */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Cargando notificaciones...
              </div>
            ) : notificaciones.filter(notif => !isLeida(notif)).length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No hay notificaciones
              </div>
            ) : (
              notificaciones
                .filter(notif => !isLeida(notif))
                .map((notificacion) => (
                  <div
                    key={notificacion._id}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors bg-blue-50`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-800">
                            {notificacion.titulo}
                          </h4>
                          {!isLeida(notificacion) && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">
                          {notificacion.mensaje}
                        </p>

                        {/* Información de la cita si existe */}
                        {notificacion.tipo === 'cita' && notificacion.datosCita && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-2">
                            <div className="flex items-center gap-2 mb-1">
                              <FaCalendarAlt className="w-4 h-4 text-[#1B65A6]" />
                              <span className="text-sm font-medium text-gray-700">
                                {new Date(notificacion.datosCita.fecha).toLocaleDateString()} - {notificacion.datosCita.hora}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-1">
                              <FaUser className="w-4 h-4 text-[#1B65A6]" />
                              <span className="text-sm text-gray-600">
                                {notificacion.datosCita.paciente?.nombre || notificacion.datosCita.paciente || 'Paciente'}
                              </span>
                            </div>
                            
                            {notificacion.datosCita.paciente?.telefono && (
                              <div className="flex items-center gap-2 mb-1">
                                <FaPhone className="w-4 h-4 text-[#1B65A6]" />
                                <span className="text-sm text-gray-600">
                                  {notificacion.datosCita.paciente.telefono}
                                </span>
                              </div>
                            )}
                            
                            {notificacion.datosCita.paciente?.email && (
                              <div className="flex items-center gap-2">
                                <FaEnvelope className="w-4 h-4 text-[#1B65A6]" />
                                <span className="text-sm text-gray-600">
                                  {notificacion.datosCita.paciente.email}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            {formatearFecha(notificacion.fechaCreacion)}
                          </span>
                          
                          {!isLeida(notificacion) && (
                            <button
                              onClick={() => marcarComoLeida(notificacion._id)}
                              className="text-xs text-[#1B65A6] hover:underline flex items-center gap-1"
                            >
                              <FaCheck className="w-3 h-3" />
                              Marcar como leída
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;